import React, { useState, useEffect, useRef } from 'react';
import { ArrowDown, ArrowUp, RefreshCcw, User, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { getTokenTransactions, getDetailedFirstBuyers, getTokenDetailedStats, getTokenInfo, getAllTokenTransactions } from '../services/solanatrackerService';
import wsService from '../services/websocketService';
import { Token } from '../types/supabase';

// Define the transaction interface
interface Transaction {
  tx: string;         // Transaction signature
  amount: number;     // Token amount
  priceUsd: number;   // Price in USD
  volume: number;     // Volume in USD
  type: 'buy' | 'sell'; // Transaction type
  wallet: string;     // Wallet address
  time: number;       // Transaction timestamp
  program: string;    // Program used (e.g., "pump", "raydium")
}

// Define token stats interface
interface TokenStats {
  marketCap?: number;
  fdv?: number;
  liquidity?: number;
  volume24h?: number;
  price?: number;
  holders?: number;
  [key: string]: any;
}

// Define the first buyer interface
interface FirstBuyer {
  wallet: string;
  first_buy_time: number;
  last_transaction_time: number;
  held: number;
  sold: number;
  holding: number;
  realized: number;
  unrealized: number;
  total: number;
  total_invested: number;
  sold_usd: number;
  total_transactions: number;
  buy_transactions: number;
  sell_transactions: number;
  average_buy_amount: number;
  current_value: number;
  cost_basis: number;
}

interface TokenTransactionsProps {
  selectedToken?: Token;
  maxTransactions?: number;
  maxHistoryTransactions?: number; // New prop for controlling initial history load
}

const TokenTransactions: React.FC<TokenTransactionsProps> = ({ 
  selectedToken, 
  maxTransactions = 50,
  maxHistoryTransactions = 200 // Load up to 200 historical transactions by default
}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [firstBuyers, setFirstBuyers] = useState<FirstBuyer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'transactions' | 'firstBuyers'>('transactions');
  const [newTransactionCount, setNewTransactionCount] = useState<number>(0);
  const [tokenStats, setTokenStats] = useState<TokenStats>({});
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(false);
  const listRef = useRef<HTMLDivElement>(null);
  const socketUnsubscribeFunctions = useRef<Array<() => void>>([]);
  const updateInterval = useRef<NodeJS.Timeout | null>(null);
  
  // Track current token for cleanup
  const currentTokenRef = useRef<string | null>(null);

  // Load initial token stats and transaction data
  useEffect(() => {
    // Clean up previous WebSocket subscriptions when token changes
    if (currentTokenRef.current && currentTokenRef.current !== selectedToken?.mint) {
      console.log(`Unsubscribing from previous token: ${currentTokenRef.current}`);
      cleanupSocketSubscriptions(currentTokenRef.current);
    }
    
    if (selectedToken?.mint) {
      // Set current token for later cleanup
      currentTokenRef.current = selectedToken.mint;
      
      // Start with a clean slate
      setTransactions([]);
      setFirstBuyers([]);
      setTokenStats({});
      setError(null);
      
      // Load initial data
      fetchTokenStats(selectedToken.mint);
      fetchCompleteTransactionHistory(selectedToken.mint);
      fetchFirstBuyers(selectedToken.mint);
      
      // Set up WebSocket subscriptions
      setupWebSocketSubscriptions(selectedToken.mint);
    } else {
      // Clear data if no token is selected
      setTransactions([]);
      setFirstBuyers([]);
      setTokenStats({});
      currentTokenRef.current = null;
    }
    
    // Cleanup when component unmounts
    return () => {
      if (currentTokenRef.current) {
        cleanupSocketSubscriptions(currentTokenRef.current);
      }
    };
  }, [selectedToken?.mint, maxHistoryTransactions]);

  // Clean up WebSocket subscriptions for a token
  const cleanupSocketSubscriptions = (tokenAddress: string) => {
    wsService.leaveRoom(`transaction:${tokenAddress}`);
    wsService.leaveRoom(`price:${tokenAddress}`);
    wsService.leaveRoom(`stats:${tokenAddress}`);
    console.log(`Cleaned up subscriptions for ${tokenAddress}`);
    
    // Call all unsubscribe functions
    socketUnsubscribeFunctions.current.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (e) {
        console.error('Error unsubscribing from WebSocket:', e);
      }
    });
    
    // Clear the list
    socketUnsubscribeFunctions.current = [];
    
    // Clear any polling interval
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
      updateInterval.current = null;
    }
  };
  
  // Set up WebSocket subscriptions for real-time updates
  const setupWebSocketSubscriptions = (tokenAddress: string) => {
    // Make sure token address is provided
    if (!tokenAddress) {
      console.warn('Cannot set up WebSocket subscriptions: no token address provided');
      return;
    }

    // Check if token is in any of the active feeds before subscribing
    const isInActiveFeeds = () => {
      try {
        // Check localStorage for token feed membership
        const newTokens = JSON.parse(localStorage.getItem('ekko_new_tokens') || '[]');
        const trendingTokens = JSON.parse(localStorage.getItem('ekko_trending_tokens') || '[]');
        const migratedTokens = JSON.parse(localStorage.getItem('ekko_migrated_tokens') || '[]');
        
        // Check if token is in any of these lists
        return newTokens.includes(tokenAddress) || 
               trendingTokens.includes(tokenAddress) || 
               migratedTokens.includes(tokenAddress);
      } catch (error) {
        console.error('Error checking feed membership:', error);
        // If we can't check, default to allowing subscription
        return true;
      }
    };

    // If token is not in active feeds, don't set up subscriptions
    if (!isInActiveFeeds()) {
      console.log(`Token ${tokenAddress} is not in active feeds, skipping WebSocket subscriptions`);
      return;
    }

    console.log(`Setting up WebSocket subscriptions for ${tokenAddress}`);
    
    // Ensure WebSocket connection is active
    wsService.ensureConnection().then(connected => {
      if (connected) {
        console.log(`WebSocket connected, subscribing to ${tokenAddress} updates`);
        
        // Subscribe to transaction updates
        const unsubscribeTransactions = wsService.subscribeToTransactions(tokenAddress, handleNewTransaction);
        
        // Store the unsubscribe function
        socketUnsubscribeFunctions.current.push(unsubscribeTransactions);
        
        // Subscribe to stats updates
        const unsubscribeStats = wsService.subscribeToStats(tokenAddress, handleStatsUpdate);
        socketUnsubscribeFunctions.current.push(unsubscribeStats);
        
        // Subscribe to price updates
        const unsubscribePrice = wsService.subscribeToPrice(tokenAddress, handlePriceUpdate);
        socketUnsubscribeFunctions.current.push(unsubscribePrice);
      } else {
        console.warn(`WebSocket connection failed for ${tokenAddress}, using fallback methods`);
        // Fall back to periodic polling for updates
        startPeriodicUpdates(tokenAddress);
      }
    });
  };
  
  // Handle new transaction from WebSocket
  const handleNewTransaction = (data: Transaction) => {
    // Enhanced debug logging
    console.log(`Received transaction data:`, data);
    
    // Validate transaction data has minimum required fields
    if (!data || !data.tx) {
      console.debug(`Received invalid transaction data for ${selectedToken?.mint}:`, data);
      return;
    }
    
    console.log(`New transaction for ${selectedToken?.mint}:`, data);
    
    // Add visual indicator of new transaction
    setNewTransactionCount(prev => prev + 1);
    
    // Process transaction data - ensure it has all required fields
    const processedTx: Transaction = {
      ...data,
      time: data.time || Date.now(),
      tx: data.tx || `unknown-${Date.now()}`,
      type: data.type || 'buy',
      amount: data.amount || 0,
      priceUsd: data.priceUsd || 0,
      volume: data.volume || 0,
      wallet: data.wallet || 'unknown',
      program: data.program || 'unknown'
    };
    
    // Add new transaction to the list, keeping only maxTransactions
    setTransactions(prevTransactions => {
      // Check if transaction already exists to avoid duplicates
      if (prevTransactions.some(tx => tx.tx === processedTx.tx)) {
        return prevTransactions;
      }
      const updatedTransactions = [processedTx, ...prevTransactions].slice(0, maxTransactions);
      return updatedTransactions;
    });
    
    // Scroll to top when new transaction arrives
    if (listRef.current && activeTab === 'transactions') {
      listRef.current.scrollTop = 0;
    }
    
    // Refresh token stats to get updated market cap after significant transactions
    if (data.volume && data.volume > 1000) { // Only refresh stats for larger transactions
      fetchTokenStats(selectedToken?.mint || '');
    }
  };
  
  // Handle stats update from WebSocket
  const handleStatsUpdate = (statsData: any) => {
    // Validate stats data has at least one meaningful field
    if (!statsData || (
      statsData.marketCap === undefined && 
      statsData.volume24h === undefined && 
      statsData.holders === undefined && 
      statsData.price === undefined
    )) {
      console.debug(`Received invalid stats data for ${selectedToken?.mint}:`, statsData);
      return;
    }
    
    console.log(`Received stats update for ${selectedToken?.mint}:`, statsData);
    
    // Update token stats directly from the WebSocket - only update valid fields
    setTokenStats(prevStats => {
      const newStats = { ...prevStats };
      
      // Only update fields that actually exist in the message
      if (statsData.marketCap !== undefined) newStats.marketCap = statsData.marketCap;
      if (statsData.volume24h !== undefined) newStats.volume24h = statsData.volume24h;
      if (statsData.holders !== undefined) newStats.holders = statsData.holders;
      if (statsData.price !== undefined) newStats.price = statsData.price;
      
      return newStats;
    });
  };
  
  // Handle price update from WebSocket
  const handlePriceUpdate = (priceData: any) => {
    // Validate price data
    if (!priceData || priceData.price === undefined) {
      console.debug(`Received invalid price data for ${selectedToken?.mint}:`, priceData);
      return;
    }
    
    console.log(`Received price update for ${selectedToken?.mint}:`, priceData);
    
    // Update price in token stats
    setTokenStats(prevStats => ({
      ...prevStats,
      price: priceData.price
    }));
  };

  // Fetch complete transaction history using pagination
  const fetchCompleteTransactionHistory = async (tokenAddress: string) => {
    setIsLoadingHistory(true);
    setError(null);
    
    try {
      console.log(`Loading complete transaction history for ${tokenAddress}`);
      const result = await getAllTokenTransactions(tokenAddress, maxHistoryTransactions);
      
      if (result.status === 'success' && result.data && result.data.trades) {
        console.log(`Loaded ${result.data.trades.length} historical transactions`);
        
        // Set the transactions
        setTransactions(result.data.trades);
        
        // Check if there might be more data
        setHasMoreHistory(result.data.trades.length >= maxHistoryTransactions);
      } else {
        console.warn('Failed to load transaction history:', result);
        setError('Failed to load complete transaction history');
      }
    } catch (error) {
      console.error('Error loading complete transaction history:', error);
      setError('Error loading transaction history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Reset new transaction counter when tab is selected
  useEffect(() => {
    setNewTransactionCount(0);
  }, [activeTab]);

  // Set up a periodic refresh of token stats (every 60 seconds) as a fallback
  useEffect(() => {
    if (!selectedToken?.mint) return;
    
    const statsInterval = setInterval(() => {
      fetchTokenStats(selectedToken.mint);
    }, 60000);
    
    return () => {
      clearInterval(statsInterval);
    };
  }, [selectedToken?.mint]);

  // Fetch token stats, including market cap
  const fetchTokenStats = async (tokenAddress: string) => {
    try {
      console.log(`Fetching token stats for ${tokenAddress}`);
      const statsResult = await getTokenDetailedStats(tokenAddress);
      const infoResult = await getTokenInfo(tokenAddress);
      
      if (statsResult.status === 'success' && statsResult.data) {
        console.log('Token stats received:', statsResult.data);
        setTokenStats(statsResult.data);
      } else {
        console.warn('No token stats returned');
        
        // If detailed stats failed, try to get basic info which might contain some stats
        if (infoResult && infoResult.token) {
          const basicStats: TokenStats = {};
          
          // Use type assertions to handle possibly undefined properties
          const tokenInfo = infoResult.token as any;
          if (tokenInfo.marketCap) basicStats.marketCap = tokenInfo.marketCap;
          if (tokenInfo.fdv) basicStats.fdv = tokenInfo.fdv;
          if (tokenInfo.liquidity) basicStats.liquidity = tokenInfo.liquidity;
          
          console.log('Basic token stats from info:', basicStats);
          setTokenStats(basicStats);
        }
      }
    } catch (error) {
      console.error('Error fetching token stats:', error);
    }
  };

  // Fetch first buyers data
  const fetchFirstBuyers = async (tokenAddress: string) => {
    try {
      const result = await getDetailedFirstBuyers(tokenAddress);
      
      if (result.status === 'success' && result.data && Array.isArray(result.data)) {
        // Sort by first buy time (earliest first)
        const sortedBuyers = result.data
          .sort((a: FirstBuyer, b: FirstBuyer) => a.first_buy_time - b.first_buy_time);
        
        console.log(`Loaded ${sortedBuyers.length} first buyers for ${tokenAddress}`);
        setFirstBuyers(sortedBuyers);
      } else {
        console.warn('No first buyers data returned:', result);
        setFirstBuyers([]);
      }
    } catch (error) {
      console.error('Error fetching first buyers:', error);
      setFirstBuyers([]);
    }
  };

  // Format timestamp to readable date
  const formatTime = (timestamp: number): string => {
    if (!timestamp) return 'Unknown';
    try {
      // Use built-in date formatting instead of date-fns
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid date';
    }
  };

  // Format address for display (first 4 and last 4 characters)
  const formatAddress = (address: string): string => {
    if (!address) return 'Unknown';
    if (address.length <= 10) return address;
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };

  // Format number to USD
  const formatUsd = (value: number): string => {
    if (value === undefined || value === null) return '$0';
    
    // If value is less than 1, show more decimal places
    const digits = value < 1 ? 4 : 2;
    
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: digits
    }).format(value);
  };
  
  // Format market cap with K/M/B suffix
  const formatMC = (value?: number): string => {
    if (!value) return '$0';
    
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    } else {
      return `$${value.toFixed(2)}`;
    }
  };
  
  // Format timestamp to age (like "12s" for 12 seconds)
  const formatAge = (timestamp: number): string => {
    if (!timestamp) return '';
    
    const now = Date.now();
    const secondsAgo = Math.floor((now - timestamp) / 1000);
    
    if (secondsAgo < 60) {
      return `${secondsAgo}s`;
    } else if (secondsAgo < 3600) {
      return `${Math.floor(secondsAgo / 60)}m`;
    } else if (secondsAgo < 86400) {
      return `${Math.floor(secondsAgo / 3600)}h`;
    } else {
      return `${Math.floor(secondsAgo / 86400)}d`;
    }
  };

  // Render tab navigation
  const renderTabs = () => (
    <div className="flex border-b border-white/5">
      <button
        className={`px-4 py-2 font-medium text-xs relative ${
          activeTab === 'transactions' 
            ? 'text-[#47be8f] border-b-2 border-[#47be8f]' 
            : 'text-gray-400 hover:text-gray-300'
        }`}
        onClick={() => setActiveTab('transactions')}
      >
        Trades
        {activeTab !== 'transactions' && newTransactionCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-[#47be8f] rounded-full"></span>
        )}
      </button>
      <button
        className={`px-4 py-2 font-medium text-xs ${
          activeTab === 'firstBuyers' 
            ? 'text-[#47be8f] border-b-2 border-[#47be8f]' 
            : 'text-gray-400 hover:text-gray-300'
        }`}
        onClick={() => setActiveTab('firstBuyers')}
      >
        First Buyers
      </button>
    </div>
  );

  // Render transaction table
  const renderTransactions = () => (
    <div ref={listRef} className="overflow-y-auto h-full">
      {transactions.length > 0 ? (
        <div className="w-full">
          {/* Table header */}
          <div className="sticky top-0 grid grid-cols-4 px-2 py-1.5 text-xs font-medium text-gray-400 border-b border-white/5 bg-black/40 backdrop-blur-sm z-10">
            <div>Amount</div>
            <div>MC</div>
            <div>Trader</div>
            <div>Age</div>
          </div>
          
          {/* Loading indicator for historical data */}
          {isLoadingHistory && (
            <div className="py-2 px-4 bg-white/5 text-center text-xs text-gray-400">
              <RefreshCcw size={12} className="inline-block mr-2 animate-spin" />
              Loading transaction history...
            </div>
          )}
          
          {/* Table rows */}
          <div className="divide-y divide-white/5">
            {transactions.map((tx, index) => {
              if (!tx) return null;
              
              // Calculate text color based on transaction type  
              const textColor = tx.type === 'buy' 
                ? 'text-[#47be8f]' 
                : 'text-[#ef4444]';

              // Add pulse animation for very recent transactions
              const isRecent = (Date.now() - tx.time) < 5000; // 5 seconds
              const animation = isRecent ? 'animate-pulse bg-white/5' : '';
              
              return (
                <div
                  key={tx.tx || index}
                  className={`grid grid-cols-4 px-2 py-1.5 text-xs hover:bg-white/5 transition-colors ${animation}`}
                >
                  <div className={`${textColor} font-medium`}>
                    {formatUsd(tx.volume || 0)}
                  </div>
                  <div className="text-gray-400">
                    {formatMC(tokenStats.marketCap)}
                  </div>
                  <div>
                    <a 
                      href={tx.wallet ? `https://solscan.io/account/${tx.wallet}` : '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-[#47be8f] transition-colors"
                    >
                      {formatAddress(tx.wallet)}
                    </a>
                  </div>
                  <div className="text-gray-400">
                    {formatAge(tx.time)}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Load more button if there might be more history */}
          {hasMoreHistory && !isLoadingHistory && (
            <div className="py-3 text-center">
              <button 
                onClick={() => fetchCompleteTransactionHistory(selectedToken?.mint || '')}
                className="px-3 py-1.5 bg-white/5 text-[#47be8f] rounded text-xs hover:bg-white/10 transition-colors"
              >
                Load More History
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
          {loading || isLoadingHistory ? (
            <div className="flex items-center">
              <RefreshCcw size={16} className="animate-spin mr-2 text-[#47be8f]" />
              <span>Loading transactions...</span>
            </div>
          ) : error ? (
            <div className="text-center">
              <p>{error}</p>
              {selectedToken?.mint && (
                <button 
                  onClick={() => fetchCompleteTransactionHistory(selectedToken.mint)}
                  className="mt-2 px-3 py-1.5 bg-white/5 text-[#47be8f] rounded text-xs hover:bg-white/10 transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          ) : (
            <div className="text-center">
              <TrendingUp size={24} className="mb-2 text-gray-600 mx-auto" />
              <p>No transactions found</p>
              {selectedToken?.mint && (
                <p className="text-xs mt-1 text-gray-500">Waiting for real-time transaction data...</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render first buyers list with improved styling
  const renderFirstBuyers = () => (
    <div className="overflow-y-auto h-full">
      {firstBuyers.length > 0 ? (
        <div className="space-y-1 p-1">
          {firstBuyers.map((buyer, index) => {
            if (!buyer) return null;
            return (
              <div 
                key={buyer.wallet || index} 
                className="bg-white/5 backdrop-blur-sm rounded-md p-3 flex flex-col space-y-2 hover:bg-white/10 transition-colors"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <User size={14} className="mr-2 text-[#47be8f]" />
                    <a 
                      href={buyer.wallet ? `https://solscan.io/account/${buyer.wallet}` : '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-sm text-gray-300 hover:text-[#47be8f] transition-colors"
                    >
                      {formatAddress(buyer.wallet)}
                    </a>
                  </div>
                  <div className="text-xs bg-[#47be8f]/10 text-[#47be8f] px-2 py-1 rounded">
                    #{index + 1} Buyer
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex flex-col">
                    <span className="text-gray-400">First Buy</span>
                    <span className="text-white">{formatTime(buyer.first_buy_time)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-400">Last Transaction</span>
                    <span className="text-white">{formatTime(buyer.last_transaction_time)}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex flex-col">
                    <span className="text-gray-400">Invested</span>
                    <span className="text-white">{formatUsd(buyer.total_invested || 0)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-400">Profit/Loss</span>
                    <span className={(buyer.total || 0) >= 0 ? 'text-[#47be8f]' : 'text-[#ef4444]'}>
                      {formatUsd(buyer.total || 0)}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-xs border-t border-white/10 pt-2">
                  <div className="flex flex-col items-center">
                    <span className="text-gray-400">Buys</span>
                    <span className="text-[#47be8f]">{buyer.buy_transactions || 0}</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-gray-400">Sells</span>
                    <span className="text-[#ef4444]">{buyer.sell_transactions || 0}</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-gray-400">Total</span>
                    <span className="text-white">{buyer.total_transactions || 0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
          {loading ? (
            <div className="flex items-center">
              <RefreshCcw size={16} className="animate-spin mr-2 text-[#47be8f]" />
              <span>Loading first buyers...</span>
            </div>
          ) : (
            <div className="text-center">
              <User size={24} className="mb-2 text-gray-600 mx-auto" />
              <p>No first buyer data available</p>
              {selectedToken?.mint && (
                <button 
                  onClick={() => fetchFirstBuyers(selectedToken.mint)}
                  className="mt-2 px-3 py-1.5 bg-white/5 text-[#47be8f] rounded text-xs hover:bg-white/10 transition-colors"
                >
                  Refresh
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Start periodic updates as a fallback when WebSocket is not available
  const startPeriodicUpdates = (tokenAddress: string) => {
    // Clear any existing interval
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
      updateInterval.current = null;
    }
    
    // Set up periodic polling for updates (every 15 seconds)
    updateInterval.current = setInterval(() => {
      console.log(`Polling for updates for ${tokenAddress}`);
      fetchTokenStats(tokenAddress);
      // Fetch recent transactions
      getTokenTransactions(tokenAddress)
        .then((result: { status: string; data?: { trades: Transaction[] } }) => {
          if (result.status === 'success' && result.data?.trades) {
            setTransactions(prevTransactions => {
              // Combine with existing transactions, remove duplicates
              const combinedTrades = [...(result.data?.trades || []), ...prevTransactions];
              // Remove duplicates by tx hash
              const uniqueTrades = combinedTrades.filter((trade, index, self) =>
                index === self.findIndex(t => t.tx === trade.tx)
              );
              // Sort by time (newest first)
              uniqueTrades.sort((a, b) => b.time - a.time);
              // Limit to max transactions
              return uniqueTrades.slice(0, maxTransactions);
            });
          }
        })
        .catch((error: Error) => console.error('Error polling for transactions:', error));
    }, 15000);
  };

  // Show placeholder if no token is selected
  if (!selectedToken) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-black/40 backdrop-blur-sm text-gray-400 p-4">
        <TrendingUp size={24} className="mb-2 text-gray-600" />
        <p>Select a token to view transactions</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-black/40 backdrop-blur-sm">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-white/5 backdrop-blur-sm">
        <h3 className="font-medium text-white text-xs">
          {selectedToken.symbol || selectedToken.name || 'Token'} Transactions
          {newTransactionCount > 0 && activeTab !== 'transactions' && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-[#47be8f] text-black rounded-full">
              +{newTransactionCount}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <div className="text-xs text-gray-400">
            MC: {formatMC(tokenStats.marketCap)}
          </div>
          {selectedToken?.mint && (
            <button 
              onClick={() => {
                fetchTokenStats(selectedToken.mint);
                fetchCompleteTransactionHistory(selectedToken.mint);
                fetchFirstBuyers(selectedToken.mint);
              }}
              className="p-1 text-gray-400 hover:text-[#47be8f] transition-colors"
              title="Refresh data"
            >
              <RefreshCcw size={12} />
            </button>
          )}
        </div>
      </div>
      
      {renderTabs()}
      
      <div className="flex-1 overflow-hidden">
        {activeTab === 'transactions' ? renderTransactions() : renderFirstBuyers()}
      </div>
    </div>
  );
};

export default TokenTransactions; 